import type { Ref } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

import { useTranslation } from 'react-i18next';

import type { Client, Contact } from '~/api/clients/types';

import { useCachedCreditsafeRecord } from '~/api/clients';
import { Button, Card, Icon } from '~/components';
import { AnchorTargets } from '~/constants/url';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { formatVatNumber } from '~/utils/string';

import { CreditRating } from '../../../clients/CreditRating/CreditRating';
import { ClientTypeIcon } from '../ClientTypeIcon/ClientTypeIcon';
import { useNavigateWithRedirect } from '../useNavigateWithRedirect';
import {
  buttonStyle,
  cardStyle,
  headerRowStyle,
  identityStyle,
  labelStyle,
  nameStyle,
  notesStyle,
  peppolStyle,
  sectionStyle,
  subStyle,
} from './SelectedClientCard.css';

export type ClientCardDomain = 'invoices' | 'quotations';

type Props<T extends FieldValues> = {
  client: Client;
  contact: Contact;
  controlId?: string;
  controlRef?: Ref<HTMLButtonElement>;
  domain: ClientCardDomain;
  form: UseFormReturn<T>;
  onRemove: () => void;
};

export const SelectedClientCard = <T extends FieldValues>({ client, contact, controlId, controlRef, domain, form, onRemove }: Props<T>) => {
  const { t } = useTranslation(['clients', 'common', 'invoices']);
  const navigateWithRedirect = useNavigateWithRedirect(form);

  const cachedCreditsafeRecord = useCachedCreditsafeRecord(client);
  const creditsafe =
    client.clientType === 'organisation'
      ? { record: client.creditsafeRecord ?? cachedCreditsafeRecord, status: client.creditsafeStatus }
      : null;

  const headerName = client.clientType === 'organisation' || client.isInformalAssociation ? client.name : contact.fullName;
  const identifier = client.vatNumber ? formatVatNumber(client.vatNumber) : (client.otherNumber ?? null);

  const sentViaPeppol = domain === 'invoices' && client.peppolId !== null;

  const messageType = domain === 'invoices' ? 'invoices & credit notes' : 'quotations';
  const receiverEmails = contact.emails.filter(({ messages }) => messages.includes(messageType));
  const displayedEmails = receiverEmails.length > 0 ? receiverEmails : contact.emails;

  const addReceiver = () =>
    navigateWithRedirect(r(routes.editContact, { clientId: client.id, contactId: contact.id }), AnchorTargets.CONTACT_EMAILS);

  return (
    <Card.Root className={cardStyle} size="small" variant="primary-accent">
      <div className={headerRowStyle}>
        <ClientTypeIcon clientType={client.clientType} />

        <span className={identityStyle}>
          <span className={nameStyle}>{headerName}</span>

          {identifier && <span className={subStyle}>{identifier}</span>}
        </span>

        <Button
          aria-label={t('common:actions.delete')}
          className={buttonStyle}
          emphasis="tertiary"
          icon="CloseSmall"
          id={controlId}
          onClick={onRemove}
          ref={controlRef}
        />
      </div>

      <div className={sectionStyle}>
        {(client.clientType === 'organisation' || client.isInformalAssociation) && <span className={nameStyle}>{contact.fullName}</span>}

        {sentViaPeppol ? (
          <span className={peppolStyle}>
            <Icon inline name="Peppol" size={18} />
            {t('invoices:createEdit.clientInfo.peppol')}
          </span>
        ) : (
          displayedEmails.map(({ email }) => (
            <span className={subStyle} key={email}>
              {email}
            </span>
          ))
        )}

        {!sentViaPeppol && (
          <div>
            <Button emphasis="tertiary" icon="Add" onClick={addReceiver}>
              {t('invoices:createEdit.clientInfo.addEmailReceiver')}
            </Button>
          </div>
        )}
      </div>

      {contact.comments && (
        <div className={sectionStyle}>
          <span className={labelStyle}>{t('clients:createEdit.subsections.comments.title')}</span>
          <p className={notesStyle}>{contact.comments}</p>
        </div>
      )}

      {creditsafe?.record && (
        <CreditRating creditRating={creditsafe.record.creditRating ?? undefined} creditsafeStatus={creditsafe.status} />
      )}
    </Card.Root>
  );
};
